import {
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type LeaderboardScope,
  leaderboardSchema,
  type Variant,
} from "@app/protocol";
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../config";
import { useStore } from "../store";

export interface BoardState {
  readonly status: "loading" | "ready" | "failed";
  /** The last good answer; kept while the next one loads or fails. */
  readonly board: LeaderboardResponse | null;
}

/** The server's answer, checked against the protocol; null when it fails or does not fit. */
async function fetchBoard(
  session: string,
  period: LeaderboardPeriod,
  variant: Variant,
  scope: LeaderboardScope,
): Promise<LeaderboardResponse | null> {
  try {
    const query = new URLSearchParams({ period, variant, scope });
    const response = await fetch(`${API_BASE}/leaderboard?${query}`, {
      headers: { Authorization: `Bearer ${session}` },
    });
    if (!response.ok) return null;
    const parsed = leaderboardSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** One game's ranking for a period (slice 0020); reloads when either changes, and on retry. */
export function useLeaderboard(
  period: LeaderboardPeriod,
  variant: Variant,
  scope: LeaderboardScope,
): BoardState & { retry: () => void } {
  const session = useStore((s) => s.session);
  const [state, setState] = useState<BoardState>({ status: "loading", board: null });
  const load = useCallback(
    async (live: { current: boolean }) => {
      if (session === null) return;
      setState((s) => ({ status: "loading", board: s.board }));
      const board = await fetchBoard(session, period, variant, scope);
      if (!live.current) return;
      setState((s) =>
        board === null ? { status: "failed", board: s.board } : { status: "ready", board },
      );
    },
    [session, period, variant, scope],
  );
  useEffect(() => {
    const live = { current: true };
    void load(live);
    return () => {
      live.current = false;
    };
  }, [load]);
  const retry = useCallback(() => void load({ current: true }), [load]);
  return { ...state, retry };
}
